import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Check, ChevronsUpDown, Pencil } from "lucide-react";
import { CONTENT_SCHEMA, CONTENT_DEFAULTS } from "@/lib/content";

const SITE_FIELDS = [
  ["hero_title", "Hero Title"], ["hero_subtitle", "Hero Subtitle", true], ["hero_image", "Hero Image URL"],
  ["tagline", "Tagline"], ["contact_email", "Contact Email"], ["contact_phone", "Contact Phone"],
  ["address", "Address"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"],
  ["about", "About", true], ["spotlight_label", "Spotlight Label"],
  ["delivery_home_zip", "Delivery Home ZIP"], ["delivery_base_fee", "Delivery Base Fee ($)"],
  ["delivery_regional_surcharge", "Regional Surcharge ($)"], ["delivery_national_surcharge", "National Surcharge ($)"],
  ["delivery_free_threshold", "Free Delivery Over ($)"],
  ["delivery_per_mile", "Per-Mile Fee ($)"], ["warehouse_address", "Warehouse Address (for distance)"],
  ["delivery_origin_lat", "Warehouse Lat (optional)"], ["delivery_origin_lng", "Warehouse Lng (optional)"],
];
const SEO_FIELDS = [
  ["meta_title", "Meta Title"], ["meta_description", "Meta Description", true],
  ["keywords", "Keywords"], ["og_image", "OG Image URL"],
];

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className="text-xs text-muted2 mb-1 block">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value || ""} onChange={onChange} rows={3} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold resize-none" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={onChange} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
      )}
    </div>
  );
}

function SpotlightPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await adminApi.get("/products")).data });
  const selected = products.find((p) => p.id === value);
  return (
    <div>
      <label className="text-xs text-muted2 mb-1 block">Spotlight Product</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="settings-spotlight-picker" className="w-full flex items-center justify-between bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-left">
            <span className="truncate">{selected ? selected.name : "Select a product…"}</span>
            <ChevronsUpDown className="w-4 h-4 text-muted2 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 bg-ink-800 border-gold/30 w-[--radix-popover-trigger-width]">
          <Command className="bg-ink-800">
            <CommandInput placeholder="Search products…" className="text-cream" />
            <CommandList>
              <CommandEmpty className="text-muted2 p-3 text-sm">No product found.</CommandEmpty>
              <CommandGroup>
                {products.map((p) => (
                  <CommandItem key={p.id} value={p.name} onSelect={() => { onChange(p.id); setOpen(false); }} className="text-cream aria-selected:bg-ink-600">
                    <Check className={`w-4 h-4 mr-2 ${value === p.id ? "opacity-100 text-gold" : "opacity-0"}`} />
                    {p.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function CategoriesTab() {
  const qc = useQueryClient();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await adminApi.get("/categories")).data });
  const [drafts, setDrafts] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [newCat, setNewCat] = useState({ name: "", image: "" });
  const val = (c, k) => (drafts[c.id]?.[k] !== undefined ? drafts[c.id][k] : c[k]);
  const setVal = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));
  const src = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
  const save = async (c) => {
    if (!drafts[c.id]) return;
    await adminApi.put(`/admin/categories/${c.id}`, { name: val(c, "name"), image: val(c, "image") });
    toast.success("Category updated");
    setDrafts((d) => { const n = { ...d }; delete n[c.id]; return n; });
    qc.invalidateQueries({ queryKey: ["categories"] });
  };
  const upload = async (c, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingId(c.id);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVal(c.id, "image", data.url);
      toast.success("Image uploaded — click Save");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); e.target.value = ""; }
  };
  const uploadNew = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingId("new");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewCat((n) => ({ ...n, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); e.target.value = ""; }
  };
  const createCat = async () => {
    if (!newCat.name.trim()) { toast.error("Category name required"); return; }
    await adminApi.post("/admin/categories", { name: newCat.name.trim(), image: newCat.image });
    toast.success("Category created");
    setNewCat({ name: "", image: "" });
    qc.invalidateQueries({ queryKey: ["categories"] });
  };
  const delCat = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="max-w-3xl space-y-3">
      <p className="text-sm text-muted2">Create new categories, rename them, change their image, or delete unused ones. Changes appear instantly on the homepage grid and catalog filters.</p>
      <div className="gold-card !border-gold/30 p-4 space-y-2" data-testid="settings-category-create">
        <h3 className="font-serif text-gold text-sm">Create a new category</h3>
        <div className="flex gap-2">
          <input data-testid="settings-new-category-name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="e.g. Tables, Chairs, Lounge Furniture" className="flex-1 bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
          <label className="btn-silver !py-1.5 !px-3 text-xs cursor-pointer inline-flex items-center whitespace-nowrap">
            <input type="file" accept="image/*" className="hidden" onChange={uploadNew} disabled={uploadingId === "new"} />
            {uploadingId === "new" ? "…" : newCat.image ? "Image ✓" : "Image"}
          </label>
          <button data-testid="settings-create-category" onClick={createCat} className="btn-gold !py-2 !px-4"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>
      {categories.map((c) => (
        <div key={c.id} data-testid={`settings-category-${c.slug}`} className="gold-card !border-gold/20 p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-md overflow-hidden shrink-0 border border-gold/20 bg-ink">
            {val(c, "image") ? <img src={src(val(c, "image"))} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <div className="flex-1 space-y-2">
            <input data-testid={`settings-category-name-${c.slug}`} value={val(c, "name") || ""} onChange={(e) => setVal(c.id, "name", e.target.value)} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
            <div className="flex gap-2">
              <input value={val(c, "image") || ""} onChange={(e) => setVal(c.id, "image", e.target.value)} placeholder="Image URL" className="flex-1 bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-xs font-mono focus:outline-none focus:border-gold" />
              <label className="btn-silver !py-1.5 !px-3 text-xs cursor-pointer inline-flex whitespace-nowrap">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(c, e)} disabled={uploadingId === c.id} />
                {uploadingId === c.id ? "…" : "Upload"}
              </label>
            </div>
          </div>
          <button data-testid={`settings-category-save-${c.slug}`} onClick={() => save(c)} disabled={!drafts[c.id]} className="btn-gold !py-2 !px-4 disabled:opacity-40">Save</button>
          <button data-testid={`settings-delete-category-${c.slug}`} onClick={() => delCat(c)} className="text-red-300 hover:text-red-400 shrink-0" aria-label="Delete category"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function ContentTab() {
  const qc = useQueryClient();
  const { data: content = {} } = useQuery({ queryKey: ["content"], queryFn: async () => (await adminApi.get("/content")).data });
  const [drafts, setDrafts] = useState({});
  useEffect(() => { setDrafts(content); }, [content]);
  const val = (k) => (drafts[k] !== undefined && drafts[k] !== null ? drafts[k] : CONTENT_DEFAULTS[k]);
  const setVal = (k, v) => setDrafts((d) => ({ ...d, [k]: v }));
  const save = async () => {
    await adminApi.put("/admin/content", drafts);
    toast.success("Page content saved");
    qc.invalidateQueries({ queryKey: ["content"] });
  };
  const resetKey = (k) => setVal(k, CONTENT_DEFAULTS[k]);
  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-muted2">Edit the headings, labels and copy on every public page. Leave a field blank to fall back to the original text. Click Save to publish.</p>
      {CONTENT_SCHEMA.map((group) => (
        <div key={group.page} data-testid={`content-group-${group.page}`} className="gold-card !border-gold/25 p-6 space-y-4">
          <h3 className="font-serif text-lg text-gold">{group.page}</h3>
          {group.fields.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted2">{f.label}</label>
                <button type="button" onClick={() => resetKey(f.key)} className="text-[10px] text-muted2 hover:text-gold">Reset</button>
              </div>
              {f.textarea ? (
                <textarea data-testid={`content-${f.key}`} value={val(f.key) || ""} onChange={(e) => setVal(f.key, e.target.value)} rows={3} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold resize-none" />
              ) : (
                <input data-testid={`content-${f.key}`} value={val(f.key) || ""} onChange={(e) => setVal(f.key, e.target.value)} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
              )}
            </div>
          ))}
        </div>
      ))}
      <div className="sticky bottom-4">
        <button data-testid="content-save" onClick={save} className="btn-gold w-full">Save Page Content</button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const { admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [site, setSite] = useState({});
  const [seo, setSeo] = useState({});

  const { data: siteData } = useQuery({ queryKey: ["settings"], queryFn: async () => (await adminApi.get("/settings")).data });
  const { data: seoData } = useQuery({ queryKey: ["seo"], queryFn: async () => (await adminApi.get("/seo")).data });
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs"], queryFn: async () => (await adminApi.get("/faqs")).data });
  const { data: admins = [] } = useQuery({ queryKey: ["admins"], queryFn: async () => (await adminApi.get("/admin/admins")).data });

  useEffect(() => { if (siteData) setSite(siteData); }, [siteData]);
  useEffect(() => { if (seoData) setSeo(seoData); }, [seoData]);

  const saveSite = async () => { await adminApi.put("/admin/settings", site); toast.success("Site settings saved"); qc.invalidateQueries({ queryKey: ["settings"] }); };
  const saveSeo = async () => { await adminApi.put("/admin/seo", seo); toast.success("SEO settings saved"); qc.invalidateQueries({ queryKey: ["seo"] }); };

  // FAQ state
  const [faq, setFaq] = useState({ question: "", answer: "" });
  const addFaq = async () => {
    if (!faq.question || !faq.answer) { toast.error("Question and answer required"); return; }
    await adminApi.post("/admin/faqs", faq); setFaq({ question: "", answer: "" });
    toast.success("FAQ added"); qc.invalidateQueries({ queryKey: ["faqs"] });
  };
  const delFaq = async (id) => { await adminApi.delete(`/admin/faqs/${id}`); toast.success("FAQ removed"); qc.invalidateQueries({ queryKey: ["faqs"] }); };
  const [editFaq, setEditFaq] = useState(null);
  const saveFaq = async () => {
    if (!editFaq.question || !editFaq.answer) { toast.error("Question and answer required"); return; }
    await adminApi.put(`/admin/faqs/${editFaq.id}`, { question: editFaq.question, answer: editFaq.answer });
    setEditFaq(null); toast.success("FAQ updated"); qc.invalidateQueries({ queryKey: ["faqs"] });
  };

  // Admin state
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "", role: "admin" });
  const inviteAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) { toast.error("All fields required"); return; }
    try {
      await adminApi.post("/admin/admins", newAdmin);
      setNewAdmin({ name: "", email: "", password: "", role: "admin" });
      toast.success("Admin invited"); qc.invalidateQueries({ queryKey: ["admins"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };
  const removeAdmin = async (id) => {
    try { await adminApi.delete(`/admin/admins/${id}`); toast.success("Admin removed"); qc.invalidateQueries({ queryKey: ["admins"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline-label mb-1">Configuration</p>
        <h1 className="font-serif text-3xl text-cream">Settings</h1>
      </div>

      <Tabs defaultValue="site">
        <TabsList className="bg-ink-700 border border-gold/20">
          <TabsTrigger value="site" data-testid="settings-tab-site" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Site</TabsTrigger>
          <TabsTrigger value="seo" data-testid="settings-tab-seo" className="data-[state=active]:bg-gold data-[state=active]:text-ink">SEO</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="settings-tab-faqs" className="data-[state=active]:bg-gold data-[state=active]:text-ink">FAQs</TabsTrigger>
          <TabsTrigger value="content" data-testid="settings-tab-content" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Content</TabsTrigger>
          <TabsTrigger value="categories" data-testid="settings-tab-categories" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Categories</TabsTrigger>
          <TabsTrigger value="admins" data-testid="settings-tab-admins" className="data-[state=active]:bg-gold data-[state=active]:text-ink">Admins</TabsTrigger>
        </TabsList>

        <TabsContent value="site" className="mt-6">
          <div className="gold-card !border-gold/25 p-6 space-y-4 max-w-2xl">
            {SITE_FIELDS.map(([key, label, ta]) => (
              <Field key={key} label={label} value={site[key]} textarea={ta} testid={`settings-site-${key}`} onChange={(e) => setSite({ ...site, [key]: e.target.value })} />
            ))}
            <SpotlightPicker value={site.spotlight_product_id} onChange={(v) => setSite({ ...site, spotlight_product_id: v })} />
            <button data-testid="settings-save-site" onClick={saveSite} className="btn-gold">Save Site Settings</button>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-6">
          <div className="gold-card !border-gold/25 p-6 space-y-4 max-w-2xl">
            {SEO_FIELDS.map(([key, label, ta]) => (
              <Field key={key} label={label} value={seo[key]} textarea={ta} testid={`settings-seo-${key}`} onChange={(e) => setSeo({ ...seo, [key]: e.target.value })} />
            ))}
            <button data-testid="settings-save-seo" onClick={saveSeo} className="btn-gold">Save SEO Settings</button>
          </div>
        </TabsContent>

        <TabsContent value="faqs" className="mt-6">
          <div className="max-w-2xl space-y-4">
            <div className="gold-card !border-gold/25 p-6 space-y-3">
              <input data-testid="settings-faq-question" value={faq.question} onChange={(e) => setFaq({ ...faq, question: e.target.value })} placeholder="Question" className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
              <textarea data-testid="settings-faq-answer" value={faq.answer} onChange={(e) => setFaq({ ...faq, answer: e.target.value })} placeholder="Answer" rows={2} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold resize-none" />
              <button data-testid="settings-add-faq" onClick={addFaq} className="btn-gold"><Plus className="w-4 h-4" /> Add FAQ</button>
            </div>
            {faqs.map((f) => (
              <div key={f.id} data-testid={`settings-faq-${f.id}`} className="gold-card !border-gold/20 p-4">
                {editFaq?.id === f.id ? (
                  <div className="space-y-2">
                    <input data-testid={`settings-faq-edit-question-${f.id}`} value={editFaq.question} onChange={(e) => setEditFaq({ ...editFaq, question: e.target.value })} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
                    <textarea data-testid={`settings-faq-edit-answer-${f.id}`} value={editFaq.answer} onChange={(e) => setEditFaq({ ...editFaq, answer: e.target.value })} rows={2} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold resize-none" />
                    <div className="flex gap-2">
                      <button data-testid={`settings-faq-save-${f.id}`} onClick={saveFaq} className="btn-gold !py-1.5 !px-4 text-xs">Save</button>
                      <button onClick={() => setEditFaq(null)} className="btn-silver !py-1.5 !px-4 text-xs">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="text-cream font-serif">{f.question}</p>
                      <p className="text-muted2 text-sm mt-1">{f.answer}</p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                      <button data-testid={`settings-edit-faq-${f.id}`} onClick={() => setEditFaq(f)} className="text-gold hover:text-gold-bright"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`settings-delete-faq-${f.id}`} onClick={() => delFaq(f.id)} className="text-red-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <ContentTab />
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <CategoriesTab />
        </TabsContent>

        <TabsContent value="admins" className="mt-6">
          <div className="max-w-2xl space-y-4">
            {isSuper && (
              <div className="gold-card !border-gold/25 p-6 space-y-3">
                <h3 className="font-serif text-lg text-cream">Invite Admin</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="settings-admin-name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Name" className="bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
                  <input data-testid="settings-admin-email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="Email" className="bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
                  <input data-testid="settings-admin-password" type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="Password" className="bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold" />
                  <select data-testid="settings-admin-role" value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })} className="bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold">
                    <option value="admin">admin</option>
                    <option value="super_admin">super_admin</option>
                  </select>
                </div>
                <button data-testid="settings-invite-admin" onClick={inviteAdmin} className="btn-gold"><Plus className="w-4 h-4" /> Invite</button>
              </div>
            )}
            {admins.map((a) => (
              <div key={a.id} data-testid={`settings-admin-${a.id}`} className="gold-card !border-gold/20 p-4 flex items-center justify-between">
                <div>
                  <p className="text-cream">{a.name} <span className="text-[10px] font-mono uppercase text-gold border border-gold/40 rounded-full px-2 py-0.5 ml-2">{a.role}</span></p>
                  <p className="text-muted2 text-sm">{a.email}</p>
                </div>
                {isSuper && a.id !== admin.id && (
                  <button data-testid={`settings-remove-admin-${a.id}`} onClick={() => removeAdmin(a.id)} className="text-red-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
